<template>
  <div class="bwf-root">
    <NuxtLoadingIndicator
      :height="4"
      class="over bg-gradient-to-r from-light-blue via-medium-blue to-dark-blue"
      color="false"
    />

    <div class="bwf-content">
      <NuxtPage />
    </div>
  </div>
</template>

<script setup>
import { useShopwareContext } from "@shopware-pwa/composables-next";
import { getSessionContext } from "@shopware-pwa/api-client";
import useMyBritaUser from "~/composables/useMyBritaUser";
import { onMounted } from "vue";

const { apiInstance } = useShopwareContext();
const { pushError } = useNotifications();

const { getLabelByPath } = useCommonLabels();
const { verifyLogIn } = useMyBritaUser();
const { marketSetupLinks } = useMarketSetup();

const {
  public: { disableSessionInit },
} = useRuntimeConfig();

/**
 * THIS is the version we are living in for now
 * https://github.com/shopware/frontends/blob/%40shopware-pwa/api-client%400.7.0/templates/vue-demo-store/app.vue
 */

/**
 * init shop session
 * apparently this does not have something to do with retrieving the session (which we need for auth purposes) but how complete the session is.
 * meaning we can probably simply use the anonymous session for the initialization
 */

if (disableSessionInit !== true) {
  const sessionContextData = ref();
  try {
    // this crashes during static builds.
    // can be removed once the BWF gets split into multiple workspaces
    sessionContextData.value = await getSessionContext(apiInstance);
  } catch (e) {
    console.log("APP: could not init session context (probably during static build)", e);
  }

  const session = useSessionContext(sessionContextData.value);
  const { refreshCart } = useCart();

  const error403Message = getLabelByPath("BWF/shop/shared/errors/error403");

  apiInstance._axiosInstance.interceptors.response.use(
    (response) => {
      // If the response is successful, just return it
      return response;
    },
    async (error) => {
      // Error object has already been parsed by shopware at this point
      if (error?.statusCode === 403) {
        // show error as user is not permitted to access ressource
        pushError(error403Message);
        // navigate user to anonymus page
        await navigateTo(marketSetupLinks.value?.startPage);
        // refresh session
        await session.refreshSessionContext();
        // refresh cart
        await refreshCart();
      }
      return Promise.reject(error);
    },
  );

  /**
   *  pass session to myBritaUser
   *  will observe and marry session, refresh and observe changes
   *  pretty important to pass the context here as the context above will become available to nested components through injection but not on the same level / composable siblings
   */
  useMyBritaUser(session);

  onMounted(async () => {
    try {
      // cart impressions are hard reloads, we need to verify login before fetching the cart
      await verifyLogIn();
    } catch (e) {
      console.log("APP: could not verify login");
    }
    try {
      // refresh cart once every hard load after login verification
      await refreshCart();
    } catch (e) {
      console.log("APP: could not refresh cart");
    }
  });
}

// listen for loyJoy clicks globally
useLoyJoy();
</script>

<style>
.bwf-content {
  @apply transition-opacity duration-700;
}

.bwf-root.is-loading .bwf-content {
  @apply pointer-events-none opacity-80;
}

.bwf-root.is-loading {
  @apply cursor-wait;
}

/* If the mobile navigation is opened, prevent scrollbars for the page content. */
html:has(.is-mobile-open) {
  overflow: clip;
}
</style>
